import { ProductListSource } from '@epicuro-next/data/stories';
import { BasketItemSource } from '@epicuro-next/models/basket';
import { ProductSource } from '@epicuro-next/state/config-strip-state';
import { ListEntity } from '@epicuro-next/state/list-entities';
import { Dictionary } from '@ngrx/entity';

/**
 * This method will map the product source to a GTM ready source object
 *
 * @param productSource The unmapped product source
 * @param lookBookEntitiesHashMap The hashmap of the lookbook entities which contains the GTM ready source objects, using key <lookbookId>:<listId>
 * @param listEntities The list entities object
 *
 * @return An object containing both an id and name this can be used for GTM source object, which is currently used in view_item and add_to_track event
 */
export function mapProductSource(
  productSource: ProductSource,
  lookBookEntitiesHashMap: {
    [key: string]: { lookBookName: string; listName: string };
  },
  listEntities: Dictionary<ListEntity>,
) {
  const mappedProductSourceType = { type: productSource.type };

  switch (productSource.type) {
    case BasketItemSource.LIST_SPOTLIGHT:
    case BasketItemSource.LIST_LOCAL:
    case BasketItemSource.LIST_DASHBOARD:
    case BasketItemSource.LIST_EMAIL:
    case BasketItemSource.LIST_STORY_NAVIGATION:
    case BasketItemSource.LIST_STORY_PAGE:
    case BasketItemSource.LIST_MODAL:
    case BasketItemSource.LIST_LANDING_PAGE_NEWS_FEED:
    case BasketItemSource.LIST_LANDING_PAGE_PROMOTION:
    case BasketItemSource.LIST_LANDING_PAGE: {
      if (productSource.details?.lookBookId?.[0]) {
        const lookBookGTMHasMap =
          lookBookEntitiesHashMap[
            `${productSource.details?.lookBookId?.[0]}:${productSource.details?.listId?.[0]}`
          ];
        return {
          id: lookBookGTMHasMap?.lookBookName,
          name: lookBookGTMHasMap?.listName,
          lookbookId: productSource.details?.lookBookId?.[0],
          lookbookName: lookBookGTMHasMap?.lookBookName,
          listId: productSource.details?.listId?.[0],
          listName: lookBookGTMHasMap?.listName,
          ...mappedProductSourceType,
        };
      }

      const listEntity = listEntities[productSource.details?.listId?.[0] ?? ''];
      return {
        id: listEntity?.listId,
        name: listEntity?.list?.name,
        ...mappedProductSourceType,
      };
    }
    case BasketItemSource.RECOMMENDATION_PANEL:
      return {
        ...productSource.details.recommendationPanel,
        ...mappedProductSourceType,
      };
    case BasketItemSource.BASKET:
      return {
        id: productSource.details.basketId?.[0],
        name: BasketItemSource.BASKET,
        ...mappedProductSourceType,
      };
    case ProductListSource.SEARCH:
    case BasketItemSource.SEARCH_LANDING_PAGE:
      return {
        id: productSource.type,
        name: productSource.type,
        ...mappedProductSourceType,
      };
    default:
      return {
        id: '',
        name: '',
        ...mappedProductSourceType,
      };
  }
}
